import React, { useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import Async from 'react-select/async';

import { Grid, CircularProgress, Typography } from '@material-ui/core';
import { toast } from 'react-toastify';
import { instance } from '../../services/api';
import { alignCenterXY, InputContainer } from './styles';
import { AssetInput } from '../../store/inputs/AssetInput';
import gteltTransformer from '../../services/gtelt_transformer';
import { GlobalsContext } from '../../store/Globals';

const CAssetInput = observer(({ item }: { item: AssetInput }) => {
  const {
    label,
    value,
    selectorValue,
    onChange,
    disabled,
    dataType,
    isKey,
    isEdit,
  } = item;
  const globals = useContext(GlobalsContext);
  const [keys, setKeys] = useState<Record<string, string[]>>({});
  const [collection, setCollection] = useState<Record<string, string>>({});
  const [readers, setReaders] = useState<Record<string, string[] | null>>({});
  const [loading, setLoading] = useState<boolean>(true);

  const assetType = dataType.replace('->', '');

  useEffect(() => {
    const getSchema = async () => {
      try {
        if (
          globals.assetList.some(
            (itm: { tag: string }) => itm.tag === assetType,
          ) &&
          assetType !== '@asset'
        ) {
          const { data } = await instance.post('/api/query/getSchema/', {
            assetType,
          });
          if (data.props) {
            const assetReaders: Record<string, string[] | null> = {
              [assetType]: data.readers,
            };
            setReaders(assetReaders);
            const assetKeys: Record<string, string[]> = {
              [assetType]: data.props
                .filter((itm: { isKey: boolean }) => itm.isKey)
                .map((itm: { tag: string }) => itm.tag),
            };
            setKeys(assetKeys);
            setCollection({
              [assetType]: data.collection ? data.collection : assetType,
            });
          }
        } else if (assetType === '@asset') {
          let i = 0;
          const assetKeys: Record<string, string[]> = {};
          const assetReaders: Record<string, string[] | null> = {};
          const assetCollection: Record<string, string> = {};
          while (i < globals.assetList.length) {
            const { tag } = globals.assetList[i];
            i++;
            if (tag !== 'assetTypeListData') {
              // eslint-disable-next-line no-await-in-loop
              const { data } = await instance.post('/api/query/getSchema/', {
                assetType: tag,
              });
              if (data.props) {
                assetReaders[tag] = data.readers;
                assetCollection[tag] = data.collection ? data.collection : tag;
                assetKeys[tag] = data.props
                  .filter((itm: { isKey: boolean }) => itm.isKey)
                  .map((itm: { tag: string }) => itm.tag);
              }
            }
          }
          setReaders(assetReaders);
          setKeys(assetKeys);
          setCollection(assetCollection);
        }
      } catch (error: any) {
        toast.error(error.message || 'Fail to get asset schema');
      } finally {
        setLoading(false);
      }
    };

    getSchema();
  }, [assetType, globals]);

  const getValueItem = (itm: any) => {
    return {
      ...itm,
      ...(readers && readers['@assetType'] && { '@private': true }),
    };
  };

  // Adjust value prop
  useEffect(() => {
    if (value) {
      let numberOfKeys = 0;
      if (Object.keys(value)) {
        numberOfKeys = Object.keys(value).length;
      }

      if (value) {
        onChange({
          value: numberOfKeys === 2 ? getValueItem(value) : value,
          label: globals.itemLabel(value),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, globals]);

  // Load all asset options
  const loadOptions = (input: string) => {
    return new Promise((resolve) => {
      (async () => {
        const selector: Record<string, any> = {
          selector: { '@assetType': assetType },
        };

        if (input.length)
          keys[assetType].forEach((key) => {
            selector[key] = gteltTransformer(input);
          });

        let queryReq;
        if (assetType === '@asset') {
          queryReq = {
            query: { selector: { '@assetType': { $gt: 0 } }, limit: 100 },
            resolve: false,
          };
        } else {
          queryReq = {
            query: { ...selector, limit: 100 },
            ...(readers[assetType] && { collection: collection[assetType] }),
            resolve: false,
          };
        }

        const {
          data: { result },
        } = (await instance.post(`/api/query/search`, queryReq)) as any;

        // Check if the key is an asset
        if (
          assetType !== '@asset' &&
          keys &&
          keys[assetType].length > 0 &&
          result &&
          result.length > 0 &&
          typeof result[0][keys[assetType][0]] === 'object'
        ) {
          const {
            data: { props },
          } = await instance.post('/api/query/getSchema/', {
            assetType: result[0][keys[assetType][0]]['@assetType'],
          });

          const objKey = props.find((itm: { isKey: boolean }) => itm.isKey);

          if (objKey) {
            const loadedOptions = result.map((itm: any) => ({
              value: getValueItem(itm),
              label: globals.itemLabel(itm),
            }));

            resolve(result ? loadedOptions : []);
          } else {
            const loadedOptions = result.map((itm: any) => ({
              value: getValueItem(itm),
              label: `${keys[assetType].map(
                (key) => `${itm[key]['@assetType']} `,
              )}`,
            }));

            resolve(result ? loadedOptions : []);
          }
        } else if (
          assetType === '@asset' &&
          keys &&
          result &&
          result.length > 0
          // typeof result[0][keys[assetType][0]] === 'object'
        ) {
          let i = 0;
          while (i < result.length) {
            const itm = result[i];
            const tag = itm['@assetType'];
            const {
              data: { props },
              // eslint-disable-next-line no-await-in-loop
            } = await instance.post('/api/query/getSchema/', {
              assetType: tag,
            });

            const objKey = props.find((it: { isKey: boolean }) => it.isKey);

            if (objKey) {
              const loadedOptions = result.map((it: any) => ({
                value: getValueItem(it),
                label: globals.itemLabel(it),
              }));
              resolve(result ? loadedOptions : []);
            } else {
              const loadedOptions = result.map((it: any) => ({
                value: getValueItem(itm),
                label: `${keys[tag].map((key) => `${it[key]['@assetType']} `)}`,
              }));
              resolve(result ? loadedOptions : []);
            }

            i++;
          }
        } else {
          const loadedOptions = result.map((itm: any) => ({
            value: getValueItem(itm),
            label: `${keys[assetType].map((key) => `${itm[key]} `)}`,
          }));

          resolve(result ? loadedOptions : []);
        }
      })();
    });
  };

  if (loading) return <CircularProgress />;

  return (
    <InputContainer>
      <Grid container spacing={3}>
        <Grid item xs={11} style={{ margin: '0 auto' }}>
          <Grid container spacing={3} style={alignCenterXY}>
            <Grid item xs={11}>
              <Typography style={{ color: '#777' }}>{label}</Typography>
              <Async
                isClearable
                cacheOptions
                defaultOptions
                value={selectorValue as any}
                loadOptions={loadOptions}
                placeholder={`Select the ${label}`}
                isDisabled={disabled || (isKey && isEdit)}
                onChange={(v: any) => {
                  if (v) onChange(v);
                  else onChange(null);
                }}
              />
            </Grid>

            <Grid item xs={1} style={{ ...alignCenterXY, paddingBottom: '0' }}>
              {isKey ? <i className="bx bx-key" style={{ fontSize: '18px' }}></i> : null}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </InputContainer>
  );
});

export default CAssetInput;
